import SourceSDK from '@ringcentral/sdk';
import { CreateUrlOptions, SendOptions } from '@ringcentral/sdk/lib/platform/Platform';
import { omit } from 'ramda';
import { Constructable } from '@ringcentral/rwc-utils/src/types';
import { prefixPath, DEFAULT_PREFIX, DEFAULT_PREFIX_KEY } from '@rwc/utils/path-prefix';
interface ISendOptions extends SendOptions {
    pathPrefix?: keyof typeof DEFAULT_PREFIX | string;
}

interface ICreateUrlOptions extends CreateUrlOptions {
    addToken?: boolean;
}

export function SDKGenerator<T extends Constructable>(SuperClass: T) {
    return class extends SuperClass {
        prefixPath = prefixPath;

        // createUrl ignores the value of addToken in 4.X;
        // use asyncCreateUrl if the value of addToken is true
        public async asyncCreateUrl(
            path = '',
            options: ICreateUrlOptions = { addToken: true }
        ): Promise<string> {
            const prefixUrl = prefixPath(path, DEFAULT_PREFIX_KEY);
            const addServerUrl = this.createUrl(prefixUrl, omit(['addToken'], options));
            if (options.addToken) {
                return this.signUrl(addServerUrl);
            }

            return addServerUrl;
        }

        private getDefaultOption(options: ISendOptions) {
            const defaultOption = { ...options };
            if (defaultOption.pathPrefix === undefined) {
                defaultOption.pathPrefix = DEFAULT_PREFIX_KEY;
            }

            defaultOption.url = prefixPath(defaultOption.url, defaultOption.pathPrefix);
            return omit(['pathPrefix'], defaultOption);
        }

        // jsonSend will prefix url with /restapi/v1.0 automatically, you can change it by using pathPrefix in options.
        private async jsonSend<T>(options: ISendOptions): Promise<T> {
            const newOptions = this.getDefaultOption(options);
            return this.send(newOptions).then(response => {
                if (!this.isJson(response)) {
                    throw Error('Response content-type is not of type json.');
                }

                return response.json();
            });
        }

        public sendWithDefaultOptions = async <T>(options: ISendOptions): Promise<T> =>
            this.send(this.getDefaultOption(options));

        public jsonGet = async <T>(url, query?, options?: ISendOptions): Promise<T> =>
            this.jsonSend({ method: 'GET', url, query, ...options });

        public jsonPost = async <T>(url, body?, query?, options?: ISendOptions): Promise<T> =>
            this.jsonSend({ method: 'POST', url, query, body, ...options });

        public jsonPut = async <T>(url, body?, query?, options?: ISendOptions): Promise<T> =>
            this.jsonSend({ method: 'PUT', url, query, body, ...options });

        public jsonPatch = async <T>(url, body?, query?, options?: ISendOptions): Promise<T> =>
            this.jsonSend({ method: 'PATCH', url, query, body, ...options });

        public jsonDelete = async <T>(url, query?, options?: ISendOptions): Promise<T> =>
            this.jsonSend({ method: 'DELETE', url, query, ...options });
    };
}

export const SDK = SDKGenerator(SourceSDK);
export type SDK = InstanceType<typeof SDK>;

export default SDKGenerator;
